/*
 * Verificación: la pantalla que decide si el grafo se llena o se vuelve panteón.
 * Objetivo: despejar una reunión de 90 minutos en menos de cinco.
 *
 * Split-pane, sin modales ni transiciones de página. A la izquierda el hallazgo;
 * a la derecha, siempre visible, la frase textual que lo respalda. Se resuelve sin
 * soltar el teclado: J/K mover · V verificar · X descartar (con motivo, obligatorio)
 * · E corregir y verificar · Esc salir · ⌘Z deshacer.
 *
 * El avance es optimista: la siguiente tarjeta aparece al instante y el envío va
 * detrás, con unos segundos de margen para deshacer. Si el servidor rechaza, el
 * hallazgo vuelve a la cola con el error al lado. Un hallazgo verificado es
 * inmutable, así que "deshacer" solo existe mientras no se ha enviado.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { ErrorApi, api, sesion } from "../api/cliente";
import { claves, useCandidatos, useCuentas, usePersonas, useSesiones } from "../api/hooks";
import { SESIONES, TIPOS_HALLAZGO, type Hallazgo } from "../api/tipos";
import { useAvisos } from "../shell/Aviso";
import { Abierto, Esqueleto, Kbd, Pastilla, fmtMs } from "../ui/primitivas";

const MARGEN_DESHACER_MS = 4000;

type Decision = { tipo: "aceptar"; contenido?: string } | { tipo: "rechazar"; motivo: string };

export function Verificacion() {
  const { caso = "" } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { avisar } = useAvisos();
  const { data: candidatos, isLoading } = useCandidatos(caso);
  const { data: personas } = usePersonas(caso);
  const { data: sesiones } = useSesiones(caso);
  const { data: cuentas } = useCuentas();
  const cuenta = cuentas?.find((c) => c.casos.some((k) => k.id === caso));

  // La cola es local: la API da los candidatos una vez y de ahí en adelante la
  // pantalla manda. Así el avance no espera a nadie.
  const [cola, setCola] = useState<Hallazgo[] | null>(null);
  const [idx, setIdx] = useState(0);
  const [hechos, setHechos] = useState<Map<string, Decision>>(new Map());
  const [errores, setErrores] = useState<Map<string, string>>(new Map());
  const [modo, setModo] = useState<"ver" | "editar" | "rechazar">("ver");
  const [texto, setTexto] = useState("");
  // Lo que está en el margen de deshacer: el temporizador y, junto a él, qué se
  // decidió, para poder mandarlo al desmontar sin depender de un estado viejo.
  const enMargen = useRef(new Map<string, { timer: number; h: Hallazgo; d: Decision }>());
  const inicio = useRef(Date.now());

  useEffect(() => { if (candidatos && cola === null) setCola(candidatos); }, [candidatos, cola]);

  const pendientes = useMemo(() => (cola ?? []).filter((h) => !hechos.has(h.id)), [cola, hechos]);
  const actual = pendientes[Math.min(idx, Math.max(0, pendientes.length - 1))];
  const total = cola?.length ?? 0;
  const nombre = useMemo(() => new Map((personas ?? []).map((p) => [p.id, p.nombre])), [personas]);
  const sesionDe = useMemo(() => new Map((sesiones ?? []).map((s) => [s.id, s])), [sesiones]);

  const invalidar = useCallback(() => {
    qc.invalidateQueries({ queryKey: claves.candidatos(caso) });
    qc.invalidateQueries({ queryKey: claves.verificados(caso) });
    qc.invalidateQueries({ queryKey: claves.vista(caso) });
    qc.invalidateQueries({ queryKey: claves.hoy });
  }, [qc, caso]);

  const enviar = useCallback(async (h: Hallazgo, d: Decision, keepalive = false) => {
    enMargen.current.delete(h.id);
    const base = `/api/casos/${encodeURIComponent(caso)}/hallazgos/${encodeURIComponent(h.id)}`;
    try {
      if (d.tipo === "aceptar") await api(`${base}/aceptar`, { body: { verificador_id: sesion.quien(), contenido_corregido: d.contenido ?? null }, keepalive });
      else await api(`${base}/rechazar`, { body: { verificador_id: sesion.quien(), motivo: d.motivo }, keepalive });
      invalidar();
    } catch (e) {
      // Vuelve a la cola con el error al lado: nada se pierde en silencio.
      setHechos((m) => { const n = new Map(m); n.delete(h.id); return n; });
      setErrores((m) => new Map(m).set(h.id, e instanceof ErrorApi ? e.message : "No se pudo guardar."));
    }
  }, [caso, invalidar]);

  const decidir = useCallback((h: Hallazgo, d: Decision) => {
    setHechos((m) => new Map(m).set(h.id, d));
    setErrores((m) => { const n = new Map(m); n.delete(h.id); return n; });
    setModo("ver");
    const timer = window.setTimeout(() => enviar(h, d), MARGEN_DESHACER_MS);
    enMargen.current.set(h.id, { timer, h, d });
    const etiqueta = d.tipo === "rechazar" ? "Descartado" : d.contenido ? "Corregido y verificado" : "Verificado";
    avisar(`${etiqueta} · ${h.contenido.slice(0, 60)}${h.contenido.length > 60 ? "…" : ""}`, () => {
      const pendiente = enMargen.current.get(h.id);
      if (pendiente === undefined) return; // ya se envió: verificado es inmutable
      window.clearTimeout(pendiente.timer);
      enMargen.current.delete(h.id);
      setHechos((m) => { const n = new Map(m); n.delete(h.id); return n; });
    });
  }, [enviar, avisar]);

  // Al salir, lo que quedó en el margen de deshacer se manda ya, sin esperar.
  const enviarRef = useRef(enviar);
  enviarRef.current = enviar;
  useEffect(() => {
    const m = enMargen.current;
    return () => {
      for (const { timer, h, d } of m.values()) {
        window.clearTimeout(timer);
        void enviarRef.current(h, d, true);
      }
      m.clear();
    };
  }, []);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") return; // la paleta
      if (modo !== "ver") {
        if (e.key === "Escape") { e.preventDefault(); setModo("ver"); }
        return;
      }
      if (document.querySelector(".velo")) return;
      const k = e.key.toLowerCase();
      if (e.key === "Escape") { e.preventDefault(); nav(cuenta ? `/cuentas/${cuenta.id}/evidencia` : "/hoy"); return; }
      if (!actual) return;
      if (k === "j" || e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(i + 1, pendientes.length - 1)); }
      else if (k === "k" || e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
      else if (k === "v") { e.preventDefault(); decidir(actual, { tipo: "aceptar" }); }
      else if (k === "x") { e.preventDefault(); setTexto(""); setModo("rechazar"); }
      else if (k === "e") { e.preventDefault(); setTexto(actual.contenido); setModo("editar"); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [modo, actual, pendientes.length, decidir, nav, cuenta]);

  const salir = () => nav(cuenta ? `/cuentas/${cuenta.id}/evidencia` : "/hoy");
  const hechosN = hechos.size;
  const minutos = Math.max(1, Math.round((Date.now() - inicio.current) / 60000));

  if (isLoading || cola === null) return <div className="pagina" style={{ maxWidth: 1200 }}><Esqueleto lineas={6} alto={18} /></div>;

  const sesionActual = actual ? sesionDe.get(actual.sesion_id) : undefined;
  const sesionNombre = sesionActual ? SESIONES.find((s) => s.id === sesionActual.tipo)?.nombre : actual?.sesion_id;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <header style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 24px", borderBottom: "1px solid var(--border)", background: "var(--card)" }}>
        <button onClick={salir} style={{ color: "var(--muted)", fontSize: 12.5, whiteSpace: "nowrap" }}>← {cuenta?.empresa ?? "Salir"} <Kbd>esc</Kbd></button>
        <h1 className="titulo" style={{ fontSize: 17, flex: 1 }}>Hallazgos</h1>
        <span className="mono" style={{ fontSize: 14 }}>{hechosN} / {total}</span>
      </header>

      {pendientes.length === 0 ? (
        <div className="pagina" style={{ maxWidth: 640, textAlign: "center" }}>
          <h2 className="subtitulo" style={{ marginBottom: 8 }}>{total === 0 ? "Nada por verificar." : "Cola despejada."}</h2>
          <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 16 }}>{total === 0 ? "Los hallazgos llegan después de cada sesión." : `${hechosN} hallazgos en ${minutos} min. Los últimos se están guardando.`}</p>
          <button className="btn btn-primario" onClick={salir}>Ver la evidencia →</button>
        </div>
      ) : (
        <div style={{ flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)" }}>
          {/* ── Izquierda: el hallazgo ── */}
          <section style={{ overflowY: "auto", padding: "24px 28px", borderRight: "1px solid var(--border)" }}>
            <div className="abierto" style={{ padding: "18px 20px", animation: "none" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
                <span className="tag11" style={{ color: "var(--open-text)" }}>▸ {TIPOS_HALLAZGO[actual.tipo]}</span>
                {actual.origen === "ia" && <Pastilla estado="muted">IA</Pastilla>}
                {actual.visibilidad === "anonimizada" && <Pastilla estado="muted">anonimizado</Pastilla>}
                {errores.get(actual.id) && <Pastilla estado="risk">{errores.get(actual.id)}</Pastilla>}
              </div>
              {modo === "editar" ? (
                <div className="campo">
                  <textarea autoFocus value={texto} onChange={(e) => setTexto(e.target.value)} rows={4} style={{ fontSize: 15, background: "var(--card)", color: "var(--text)" }}
                    onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); if (texto.trim()) decidir(actual, { tipo: "aceptar", contenido: texto.trim() }); } }} />
                  <span className="pista">Se guardan las dos versiones: la propuesta y la corregida. <Kbd>⌘↵</Kbd> verificar · <Kbd>esc</Kbd> cancelar</span>
                  <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                    <button className="btn btn-primario" disabled={!texto.trim()} onClick={() => decidir(actual, { tipo: "aceptar", contenido: texto.trim() })}>Verificar corregido</button>
                    <button className="btn btn-secundario" onClick={() => setModo("ver")}>Cancelar</button>
                  </div>
                </div>
              ) : (
                <p style={{ fontSize: 16, lineHeight: 1.5, color: "var(--text)" }}>{actual.contenido}</p>
              )}
              {actual.atributos && modo === "ver" && (
                <dl style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 12, fontSize: 12.5, color: "var(--muted)" }}>
                  {Object.entries(actual.atributos).filter(([k, v]) => k !== "tipo" && v !== null && v !== "" && v !== false).map(([k, v]) => (
                    <span key={k}><span className="tag11" style={{ color: "var(--faint)" }}>{k.replace(/_/g, " ")}</span> <span className="mono">{String(v)}</span></span>
                  ))}
                </dl>
              )}
              <div style={{ marginTop: 14, fontSize: 12.5, color: "var(--muted)", display: "flex", gap: 12, flexWrap: "wrap" }}>
                <span>{actual.visibilidad === "anonimizada" ? "frontline" : nombre.get(actual.persona_id) ?? actual.persona_id}</span>
                <span className="mono">{fmtMs(actual.timestamp_ms)}</span>
                <span>{sesionNombre}</span>
              </div>
            </div>

            {modo === "rechazar" && (
              <div className="tarjeta" style={{ marginTop: 14 }}>
                <div className="campo">
                  <label>Por qué se descarta</label>
                  <input autoFocus value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Es obligatorio: el log de descartados dice qué tipo de hallazgo no sirve."
                    onKeyDown={(e) => { if (e.key === "Enter" && texto.trim()) { e.preventDefault(); decidir(actual, { tipo: "rechazar", motivo: texto.trim() }); } }} />
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <button className="btn btn-peligro" disabled={!texto.trim()} onClick={() => decidir(actual, { tipo: "rechazar", motivo: texto.trim() })}>Descartar</button>
                  <button className="btn btn-secundario" onClick={() => setModo("ver")}>Cancelar</button>
                </div>
              </div>
            )}

            {modo === "ver" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 14 }}>
                {([["V", "verificar", () => decidir(actual, { tipo: "aceptar" })], ["X", "descartar", () => { setTexto(""); setModo("rechazar"); }], ["E", "corregir y verificar", () => { setTexto(actual.contenido); setModo("editar"); }], ["J / K", "siguiente / anterior", () => setIdx((i) => Math.min(i + 1, pendientes.length - 1))]] as [string, string, () => void][]).map(([k, l, fn]) => (
                  <button key={k} onClick={fn} className="tarjeta fila-hover" style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 12px", fontSize: 13 }}><Kbd>{k}</Kbd>{l}</button>
                ))}
              </div>
            )}

            <div style={{ marginTop: 22 }}>
              <span className="tag11" style={{ color: "var(--faint)", display: "block", marginBottom: 6 }}>En la cola</span>
              <div className="lista">
                {pendientes.map((h, i) => (
                  <button key={h.id} onClick={() => { setIdx(i); setModo("ver"); }} className="fila-hover" style={{ display: "flex", gap: 10, width: "100%", padding: "8px 12px", fontSize: 12.5, background: h.id === actual.id ? "var(--sunken)" : undefined, alignItems: "baseline" }}>
                    <span className="tag11" style={{ color: "var(--faint)", width: 92, flexShrink: 0 }}>{TIPOS_HALLAZGO[h.tipo]}</span>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{h.contenido}</span>
                    {errores.has(h.id) && <span style={{ color: "var(--risk)", flexShrink: 0 }}>error</span>}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* ── Derecha: la fuente, siempre visible ── */}
          <section style={{ overflowY: "auto", padding: "24px 28px", background: "var(--card)" }}>
            <h2 className="seccion-h" style={{ marginBottom: 14 }}>Fuente</h2>
            {actual.fragmento_transcripcion ? (
              <div>
                <div style={{ display: "flex", gap: 10, alignItems: "baseline", marginBottom: 8 }}>
                  <span className="mono" style={{ color: "var(--faint)" }}>{fmtMs(actual.timestamp_ms)}</span>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{actual.visibilidad === "anonimizada" ? "Frontline" : nombre.get(actual.persona_id) ?? actual.persona_id}</span>
                </div>
                <p style={{ fontSize: 15, lineHeight: 1.6, background: "var(--ice)", padding: "10px 14px", borderRadius: "var(--r-control)" }}>{actual.fragmento_transcripcion}</p>
              </div>
            ) : (
              <Abierto>Sin fragmento de transcripción: este hallazgo se capturó a mano{actual.origen === "manual_campo" ? " en campo" : ""}. Verifícalo contra la libreta o la grabación.</Abierto>
            )}
            {sesionActual && (
              <div style={{ marginTop: 20, paddingTop: 14, borderTop: "1px solid var(--border)", fontSize: 12.5, color: "var(--muted)", display: "flex", flexDirection: "column", gap: 4 }}>
                <span>{sesionNombre} · <span className="mono">{sesionActual.fecha.slice(0, 10)}</span></span>
                <span>{sesionActual.asistentes.map((a) => nombre.get(a) ?? a).join(", ")}</span>
                <span>{sesionActual.grabada ? "Grabada con consentimiento" : "Sin grabación"}</span>
              </div>
            )}
            <p style={{ marginTop: 20, fontSize: 12, color: "var(--faint)", lineHeight: 1.5 }}>Verificar es afirmar que esto se dijo y significa esto. Un hallazgo verificado es inmutable: para corregirlo después se crea la versión siguiente desde Evidencia.</p>
          </section>
        </div>
      )}
    </div>
  );
}
